import { useState, useCallback } from 'react';
import { auditLogsService } from '../services/auditLogsService';
import { getCurrentYear } from '../utils/eventStatus';

// Por defecto se muestran los movimientos del año en curso
const buildInitialFilters = () => ({
  studentId: '',
  studentName: '',
  eventId: '',
  auditLogTypes: '',
  startDate: '',
  endDate: '',
  userSede: '',
  search: '',
  year: getCurrentYear(),
  sortOrder: 'DESC'
});

const INITIAL_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
  from: 0,
  to: 0,
  currentPage: 1,
  lastPage: 1
};

/**
 * Hook para el audit log de movimientos de entradas.
 * Todo lo que está en `filters` se manda a la API: el backend corre con
 * forbidNonWhitelisted y rechaza cualquier parámetro no declarado en el DTO.
 */
export const useAuditLogsManagement = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(buildInitialFilters);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);

  const clearError = useCallback(() => setError(null), []);

  // Quitar valores vacíos antes de llamar a la API
  const cleanParams = (params) =>
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value.toString().trim() !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

  const fetchLogs = useCallback(
    async (overrideFilters = null, overridePage = null) => {
      setLoading(true);
      setError(null);

      try {
        const activeFilters = overrideFilters || filters;
        const page = overridePage || pagination.page;

        const response = await auditLogsService.getAuditLogs(
          cleanParams({ ...activeFilters, page, limit: pagination.limit })
        );

        setLogs(Array.isArray(response.data) ? response.data : []);
        setPagination((prev) => ({
          ...prev,
          page: response.page ?? page,
          limit: response.limit ?? prev.limit,
          total: response.total ?? 0,
          totalPages: response.totalPages ?? 0,
          from: response.from ?? 0,
          to: response.to ?? 0,
          currentPage: response.currentPage ?? response.page ?? page,
          lastPage: response.lastPage ?? response.totalPages ?? 1
        }));
      } catch (err) {
        setError(err.message);
        setLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.page, pagination.limit]
  );

  // Actualizar filtros sin disparar la búsqueda
  const applyFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Sembrar filtros desde el deep-link (?studentId=...&studentName=...)
  const seedFilters = useCallback((seed) => {
    setFilters((prev) => ({ ...prev, ...seed }));
  }, []);

  // Aplicar filtros y buscar desde la página 1
  const applyFiltersNow = useCallback(
    async (newFilters = null) => {
      const merged = newFilters ? { ...filters, ...newFilters } : filters;
      setFilters(merged);
      setPagination((prev) => ({ ...prev, page: 1, currentPage: 1 }));
      await fetchLogs(merged, 1);
    },
    [filters, fetchLogs]
  );

  const changePage = useCallback(
    async (newPage) => {
      setPagination((prev) => ({ ...prev, page: newPage, currentPage: newPage }));
      await fetchLogs(null, newPage);
    },
    [fetchLogs]
  );

  const clearFilters = useCallback(async () => {
    const initialFilters = buildInitialFilters();
    setFilters(initialFilters);
    setPagination((prev) => ({ ...prev, page: 1, currentPage: 1 }));
    await fetchLogs(initialFilters, 1);
  }, [fetchLogs]);

  return {
    logs,
    loading,
    error,
    filters,
    pagination,
    fetchLogs,
    applyFilters,
    applyFiltersNow,
    seedFilters,
    changePage,
    clearFilters,
    clearError
  };
};
